/* ============================================================
   ProvenanceChip — Proposal 8 Tier 2 (2026-04-25)

   Tiny inline chip that answers "where does this number come from?"
   at a glance. D1 maps broad legacy kinds into explicit source-state
   classes so fallback/local data never looks like strict book proof.

   Tier 1 shipped as module-level intro text (data-provenance hints
   in module-intros.js). Tier 2 is the per-card chip. Tier 3 (queued)
   is per-cell drill-down with the full computation trail.

   Tooltip text is OPTIONAL — when present it explains the recipe in
   plain English. When absent the chip still conveys the source class
   on its own.
   ============================================================ */
import React from 'react';
import { SOURCE_STATE_CLASSES, formatSourceStateTooltip, normalizeSourceState } from '../source-state.js';

const CLASS_META = {
  [SOURCE_STATE_CLASSES.CONTRACT_REAL]: {
    icon: 'API',
    cls: 'bg-[rgba(96,165,250,0.10)] text-accent-blue border-[rgba(96,165,250,0.35)]',
  },
  [SOURCE_STATE_CLASSES.CONTRACT_BACKED]: {
    icon: 'LEDGER',
    cls: 'bg-[rgba(96,165,250,0.10)] text-accent-blue border-[rgba(96,165,250,0.35)]',
  },
  [SOURCE_STATE_CLASSES.SOURCE_LOCAL]: {
    icon: 'SRC',
    cls: 'bg-[rgba(74,222,128,0.10)] text-accent-green border-[rgba(74,222,128,0.35)]',
  },
  [SOURCE_STATE_CLASSES.PROTECTED_PREVIEW]: {
    icon: 'PREVIEW',
    cls: 'bg-[rgba(240,180,41,0.10)] text-accent-gold border-[rgba(240,180,41,0.35)]',
  },
  [SOURCE_STATE_CLASSES.NON_AUTHORED]: {
    icon: 'HELD',
    cls: 'bg-[rgba(248,113,113,0.10)] text-accent-red border-[rgba(248,113,113,0.35)]',
  },
  [SOURCE_STATE_CLASSES.PROVIDER_LIVE_NOT_TESTED]: {
    icon: 'FX',
    cls: 'bg-[rgba(240,180,41,0.10)] text-accent-gold border-[rgba(240,180,41,0.35)]',
  },
  [SOURCE_STATE_CLASSES.MISSING_WARNING]: {
    icon: 'WARN',
    cls: 'bg-[rgba(248,113,113,0.10)] text-accent-red border-[rgba(248,113,113,0.35)]',
  },
  [SOURCE_STATE_CLASSES.SCENARIO_LOCAL]: {
    icon: 'LOCAL',
    cls: 'bg-[rgba(74,222,128,0.10)] text-accent-green border-[rgba(74,222,128,0.35)]',
  },
  [SOURCE_STATE_CLASSES.SYNTHETIC_DERIVED]: {
    icon: 'SYN',
    cls: 'bg-[rgba(248,113,113,0.10)] text-accent-red border-[rgba(248,113,113,0.35)]',
  },
  [SOURCE_STATE_CLASSES.FROZEN_LOCAL_FAKE]: {
    icon: 'FAKE',
    cls: 'bg-[rgba(248,113,113,0.10)] text-accent-red border-[rgba(248,113,113,0.35)]',
  },
  [SOURCE_STATE_CLASSES.SNAPSHOT_STATIC]: {
    icon: 'SNAP',
    cls: 'bg-[rgba(240,180,41,0.10)] text-accent-gold border-[rgba(240,180,41,0.35)]',
  },
  [SOURCE_STATE_CLASSES.COMPUTED_DERIVED]: {
    icon: 'CALC',
    cls: 'bg-[rgba(240,180,41,0.10)] text-accent-gold border-[rgba(240,180,41,0.35)]',
  },
};

const BASE_CLS = 'inline-flex items-center gap-[3px] text-[0.6rem] font-semibold tracking-[0.04em] px-[5px] py-[1px] rounded-[3px] border align-middle whitespace-nowrap select-none cursor-help';

export default function ProvenanceChip({ kind, tooltip, compact = false }) {
  const state = normalizeSourceState(kind, tooltip ? { tooltip } : {});
  const meta = CLASS_META[state.sourceClass];
  if (!meta) return null;
  const tip = formatSourceStateTooltip(state);
  return (
    <span
      className={`${BASE_CLS} ${meta.cls}`}
      title={tip}
      role="img"
      aria-label={`Source: ${state.label} — ${tip}`}
    >
      <span aria-hidden>{meta.icon}</span>
      {!compact && <span className="lowercase">{state.compactLabel}</span>}
    </span>
  );
}

/** Convenience: lookup the icon character for a kind. Useful for inline
 *  rendering inside text without the full chip wrapper. */
export function provenanceIcon(kind) {
  const state = normalizeSourceState(kind);
  return CLASS_META[state.sourceClass]?.icon ?? '';
}
